/**
 * Modal utility for displaying alerts, errors, and confirmations
 * Uses existing modal CSS classes from _modal.styl
 */

export function showErrorModal(title: string, message: string): Promise<void> {
    return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'modal';
        
        const modalContent = document.createElement('div');
        modalContent.className = 'modal__content';
        
        const modalHeader = document.createElement('div');
        modalHeader.className = 'modal__header';
        
        const heading = document.createElement('h3');
        heading.textContent = title;
        
        const closeBtn = document.createElement('button');
        closeBtn.className = 'modal__close';
        closeBtn.innerHTML = '&times;';
        
        modalHeader.appendChild(heading);
        modalHeader.appendChild(closeBtn);
        
        const modalBody = document.createElement('div');
        modalBody.className = 'modal__body';
        modalBody.textContent = message;
        
        modalContent.appendChild(modalHeader);
        modalContent.appendChild(modalBody);
        modal.appendChild(modalContent);
        document.body.appendChild(modal);
        
        const closeModal = () => {
            modal.remove();
            resolve();
        };
        
        closeBtn.addEventListener('click', closeModal);
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal();
        });
        
        // Close on Escape key
        const escapeHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                document.removeEventListener('keydown', escapeHandler);
                closeModal();
            }
        };
        document.addEventListener('keydown', escapeHandler);
    });
}

export function showConfirmModal(message: string): Promise<boolean> {
    return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'modal';
        
        const modalContent = document.createElement('div');
        modalContent.className = 'modal__content';
        
        const modalHeader = document.createElement('div');
        modalHeader.className = 'modal__header';
        
        const heading = document.createElement('h3');
        heading.textContent = 'Confirm Action';
        
        const closeBtn = document.createElement('button');
        closeBtn.className = 'modal__close';
        closeBtn.innerHTML = '&times;';
        
        modalHeader.appendChild(heading);
        modalHeader.appendChild(closeBtn);
        
        const modalBody = document.createElement('div');
        modalBody.className = 'modal__body';
        modalBody.textContent = message;
        
        // Button container
        const buttonContainer = document.createElement('div');
        buttonContainer.style.display = 'flex';
        buttonContainer.style.gap = '10px';
        buttonContainer.style.marginTop = '20px';
        buttonContainer.style.justifyContent = 'flex-end';
        
        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = 'Cancel';
        cancelBtn.className = 'btn-secondary';
        cancelBtn.style.cursor = 'pointer';
        
        const confirmBtn = document.createElement('button');
        confirmBtn.textContent = 'Confirm';
        confirmBtn.className = 'btn-primary';
        confirmBtn.style.cursor = 'pointer';
        
        buttonContainer.appendChild(cancelBtn);
        buttonContainer.appendChild(confirmBtn);
        modalBody.appendChild(buttonContainer);
        
        modalContent.appendChild(modalHeader);
        modalContent.appendChild(modalBody);
        modal.appendChild(modalContent);
        document.body.appendChild(modal);
        
        const closeModal = (result: boolean) => {
            modal.remove();
            resolve(result);
        };
        
        closeBtn.addEventListener('click', () => closeModal(false));
        cancelBtn.addEventListener('click', () => closeModal(false));
        confirmBtn.addEventListener('click', () => closeModal(true));
        
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal(false);
        });
        
        // Close on Escape key = cancel
        const escapeHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                document.removeEventListener('keydown', escapeHandler);
                closeModal(false);
            }
        };
        document.addEventListener('keydown', escapeHandler);
    });
}

export function showSuccessModal(title: string, message: string): Promise<void> {
    return showErrorModal(title, message); // Reuse same pattern for success
}
